import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useSiteContent } from "@/context/SiteContentContext";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Twitter, Linkedin, Lock } from "lucide-react";

const LOGO_URL = "/logo_transparent.png";
const LOGO_BASE_HEIGHT = 32;

export function Footer() {
  const { t, i18n } = useTranslation();
  const { content, navPages } = useSiteContent();
  const { user, loginWithGoogle } = useAuth();
  const isAr = i18n.language === "ar";

  const logoSrc = content?.logo_url || LOGO_URL;
  const logoScale = content?.logo_scale || 1.3;
  const logoHeight = Math.round(LOGO_BASE_HEIGHT * logoScale);
  const tagline = (isAr ? content?.footer?.tagline_ar : content?.footer?.tagline_en) || t("footer.tagline");
  const rights = (isAr ? content?.footer?.rights_ar : content?.footer?.rights_en) || t("footer.rights");

  return (
    <footer className="relative border-t border-border/70 bg-card mt-auto pb-24 sm:pb-0" data-testid="site-footer">
      <div className="divider-accent" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
        <div className="grid grid-cols-2 gap-8 sm:gap-12 lg:grid-cols-[1.5fr_1fr_1fr_1fr]">
          <div className="col-span-2 lg:col-span-1">
            <img
              src={logoSrc}
              alt="Logo"
              style={{ height: `${logoHeight}px` }}
              className="w-auto object-contain dark:brightness-125"
              data-testid="footer-logo"
            />
            <p className="mt-5 text-sm text-muted-foreground max-w-xs leading-relaxed">{tagline}</p>
            <div className="mt-5 flex items-center gap-3">
              <a href="https://twitter.com" target="_blank" rel="noreferrer" aria-label="X / Twitter" className="h-9 w-9 rounded-full border border-border grid place-items-center hover:border-primary hover:text-primary transition-[color,border-color] duration-150">
                <Twitter className="h-4 w-4" />
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noreferrer" aria-label="LinkedIn" className="h-9 w-9 rounded-full border border-border grid place-items-center hover:border-primary hover:text-primary transition-[color,border-color] duration-150">
                <Linkedin className="h-4 w-4" />
              </a>
            </div>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-foreground/80 mb-4">{t("footer.platform")}</h4>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li><a href="/#services" className="hover:text-foreground transition-colors" data-testid="footer-link-services">{t("footer.services")}</a></li>
              <li><Link to="/dashboard" className="hover:text-foreground transition-colors">{t("header.dashboard")}</Link></li>
              {(navPages || []).filter((p) => ["both", "footer"].includes(p.nav_placement || "both") && (p.footer_column || "company") === "platform").map((p) => (
                <li key={p.slug}>
                  <Link
                    to={p.path}
                    className="hover:text-foreground transition-colors"
                    data-testid={`footer-link-page-${p.slug}`}
                  >
                    {isAr ? p.label_ar : p.label_en}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-foreground/80 mb-4">{t("footer.company")}</h4>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li><Link to="/contact" className="hover:text-foreground transition-colors" data-testid="footer-link-contact">{t("footer.contactUs")}</Link></li>
              <li><Link to="/waitlist" className="hover:text-foreground transition-colors" data-testid="footer-link-waitlist">{t("footer.waitlist")}</Link></li>
              {(navPages || []).filter((p) => ["both", "footer"].includes(p.nav_placement || "both") && (p.footer_column || "company") === "company").map((p) => (
                <li key={p.slug}>
                  <Link
                    to={p.path}
                    className="hover:text-foreground transition-colors"
                    data-testid={`footer-link-page-${p.slug}`}
                  >
                    {isAr ? p.label_ar : p.label_en}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-foreground/80 mb-4">
              {t("footer.resources")}
            </h4>
            <ul className="space-y-3 text-sm text-muted-foreground">
              {(navPages || []).filter((p) => ["both", "footer"].includes(p.nav_placement || "both") && p.footer_column === "resources").map((p) => (
                <li key={p.slug}>
                  <Link
                    to={p.path}
                    className="hover:text-foreground transition-colors"
                    data-testid={`footer-link-page-${p.slug}`}
                  >
                    {isAr ? p.label_ar : p.label_en}
                  </Link>
                </li>
              ))}
              <li className="flex items-center gap-2 text-xs text-muted-foreground/70">
                <MapPin className="h-3.5 w-3.5 shrink-0" /> Riyadh, Saudi Arabia
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-t border-border pt-6 text-xs text-muted-foreground">
          <p>© {new Date().getFullYear()} · {rights}</p>
          <div className="flex items-center gap-4">
            {!user && (
              <button
                type="button"
                onClick={() => loginWithGoogle("/admin")}
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] uppercase tracking-widest text-muted-foreground/70 hover:text-primary hover:bg-accent/50 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                data-testid="footer-team-login"
                aria-label={t("footer.teamLoginAria", { defaultValue: "Team sign in" })}
                title={t("footer.teamLoginTitle", { defaultValue: "Team sign in — restricted access" })}
              >
                <Lock className="h-3 w-3" />
                <span>{t("footer.teamLogin", { defaultValue: isAr ? "دخول الفريق" : "Team" })}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
